/* Allocation & Billing — combined workspace.
 * Queue: Rx-verified "received" orders. Per order, the operator
 * allocates inventory lots per medicine line (manual or auto FIFO),
 * partial allocation allowed, then posts the (partial/full) bill. */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import supplyService from "../../Services/supplyService";
import apiService from "../../Services/authenticationServices";
import { useTaskCenter } from "../Layout/TaskCenterContext";
import pickListService from "./Reports/pickListService";
import { last7DaysRange, toDateTimeRange } from "./Reports/outOfStockService";
import { useWarehouse } from "../Layout/WarehouseContext";
import {
  buildInvoice,
  buildInvoiceFromSales,
  openPrintWindow,
  invoiceToPdfBlob,
  describeDocumentUploadError,
} from "./printInvoice";
import { cbFmtINR, cbFmtNum, cbExpiryStatus } from "./supplyData";
import { convertFormat } from "../../Utils/utilServices";
import {
  Button,
  Pill,
  SectionHeader,
  Eyebrow,
  LoadingState,
  ErrorState,
  EmptyState,
  InlineAlert,
  Input,
  Select,
  SearchableSelect,
  DateTimeRangeFilter,
  IconDownload,
  IconCheck,
  IconAlert,
  IconLock,
  IconUser,
  IconPackage,
  IconSearch,
} from "./supplyKit";

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const POLL_INTERVAL = 1500;
const POLL_MAX_ATTEMPTS = 40; // ~60s before timing out

const AllocationBilling = () => {
  const { runTask } = useTaskCenter();
  const [pickOpen, setPickOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [linesLoading, setLinesLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [queue, setQueue] = useState([]);
  const [lots, setLots] = useState([]);
  const [orderLines, setOrderLines] = useState({});
  const [selected, setSelected] = useState(null);

  const { code: whCode } = useWarehouse();
  const [eclinics, setEclinics] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  /* hasNext is set after each queue fetch. The order-list endpoint doesn't
     return a total count, so we use the same heuristic the rest of the app
     uses (CheReceivedOrder): "Next" is enabled whenever the current page
     returned any rows. If the user lands on an empty page, hasNext flips
     false on the next render and they can go back. */
  const [hasNext, setHasNext] = useState(false);
  /* Queue sort direction passed straight to order-list's `orderBy`. DESC =
     newest first (the default), ASC = oldest first. An order-ID search always
     uses DESC regardless of this. */
  const [sortOrder, setSortOrder] = useState("DESC");

  const [filters, setFilters] = useState({
    orderType: "CHE", // "CHE" | "B2C"
    orderId: "",
    eclinicCode: "",
    fromDate: "", // YYYY-MM-DD (from the range picker); empty = no date filter
    toDate: "",
    fromTime: "", // HH:mm (picked in the same popover); applied with the range
    toTime: "",
  });

  /* Query params shared by the order queue and the clinic dropdown, so the two
     always describe the same slice of orders. state + circleCode are persisted
     on login (Login.jsx -> localStorage).

     `eclinicCode` is deliberately NOT here: it narrows the queue, but feeding it
     to the clinic list would reduce that list to the one clinic already picked.
     Memoised on the individual filter fields rather than the whole `filters`
     object for the same reason — selecting a clinic must not refetch the list
     of clinics. */
  const baseQueueParams = useMemo(() => {
    const circleCode = localStorage.getItem("circleCode");
    const userState = localStorage.getItem("userState");
    const isB2C = filters.orderType === "B2C";
    return {
      procedureStatus: -2,
      conStatus: 1,
      state: userState || "OD",
      circleCode: circleCode || "N",
      ...(isB2C ? { orderSource: "B2C" } : {}),
      ...(filters.orderId ? { orderId: filters.orderId } : {}),
      /* Date+time window picked in one popover (DateTimeRangeFilter). Date ->
         MM/DD/YYYY (same as CheReceivedOrder); time -> HH:mm:ss (the HH:mm
         inputs are padded). Both dates must be set; each time is optional. */
      ...(filters.fromDate && filters.toDate
        ? {
          fromDate: convertFormat(filters.fromDate),
          toDate: convertFormat(filters.toDate),
          ...(filters.fromTime ? { fromTime: `${filters.fromTime}:00` } : {}),
          ...(filters.toTime ? { toTime: `${filters.toTime}:00` } : {}),
        }
        : {}),
    };
  }, [
    filters.orderType,
    filters.orderId,
    filters.fromDate,
    filters.toDate,
    filters.fromTime,
    filters.toTime,
  ]);

  /* Load the eClinic list (used when filtering CHE orders by clinic).
     hospital/eclinics-with-orders returns only clinics that actually have orders
     in the queue, and takes the same query params as the order/order-list call —
     so the dropdown offers exactly the clinics the current filters can show, and
     refreshes whenever those filters change. */
  useEffect(() => {
    let alive = true;
    apiService
      .getEclinicsWithOrders({ ...baseQueueParams, pageNumber: 1, orderBy: "DESC" })
      .then((r) => {
        if (!alive) return;
        const list = r?.data?.successList || [];
        setEclinics(list);
        /* If the clinic currently filtering the queue has no orders under the
           new filters it is gone from the list — drop it, or the dropdown would
           show a value it no longer offers while pinning the queue to empty. */
        setFilters((p) =>
          p.eclinicCode && !list.some((c) => c.code === p.eclinicCode)
            ? { ...p, eclinicCode: "" }
            : p
        );
      })
      .catch(() => {
        if (alive) setEclinics([]);
      });
    return () => {
      alive = false;
    };
  }, [reloadKey, baseQueueParams]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    const isB2C = filters.orderType === "B2C";
    /* Queue load — order/order-list with procedureStatus=-2 + conStatus=1
       (Rx-verified, ready for allocation), off the shared params above.
       eclinicCode is additive when the operator narrows to one clinic.
       Order-ID search uses orderBy=DESC + pageNumber=1 so the operator hits
       the most recent matching record immediately. */
    const isOrderIdSearch = !!filters.orderId;
    const payload = {
      ...baseQueueParams,
      pageNumber: isOrderIdSearch ? 1 : pageNumber,
      orderBy: isOrderIdSearch ? "DESC" : sortOrder,
      ...(!isB2C && filters.eclinicCode ? { eclinicCode: filters.eclinicCode } : {}),
    };
    apiService
      .getmedicineOrders(payload)
      .then((res) => {
        if (!alive) return;
        let orders = res?.data?.successList || [];
        if (isB2C) orders = orders.filter((o) => o.status !== 8);
        setQueue(orders);
        setSelected(orders[0]?.orderId || null);
        /* Drop any per-order line caches from the previous page so we don't
           pin memory. Lines for the new first-selected order are fetched by
           the line-loader effect below. */
        setOrderLines({});
        setHasNext(orders.length > 0);
        setLoading(false);
      })
      .catch((e) => {
        if (!alive) return;
        setError(e?.message || "Could not load the allocation queue.");
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [reloadKey, filters, pageNumber, sortOrder]);

  /* Cached lines belong to one warehouse — drop them when the store changes. */
  useEffect(() => {
    setOrderLines({});
  }, [whCode]);

  /* On-demand line fetch — only for the currently selected order. Cached in
     `orderLines` so re-selecting an already-loaded order is instant.
*/
  useEffect(() => {
    if (!selected) return;
    if (orderLines[selected]) return;
    let alive = true;
    setLinesLoading(true);
    apiService
      .getOrderDetails({
        patientDrugPrescriptionOrderId: selected,
        status: 1,
        ...(whCode ? { hospitalId: whCode } : {}),
      })
      .then((r) => {
        if (!alive) return;
        setOrderLines((prev) => ({
          ...prev,
          [selected]: r?.data?.successList || [],
        }));
        setLinesLoading(false);
      })
      .catch(() => {
        if (alive) setLinesLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [selected, orderLines, whCode]);

  const onResetFilters = () => {
    setPageNumber(1);
    setFilters({
      orderType: "CHE",
      orderId: "",
      eclinicCode: "",
      fromDate: "",
      toDate: "",
      fromTime: "",
      toTime: "",
    });
  };

  const order = queue.find((o) => o.orderId === selected);

  /* Refs for keyboard navigation: the scrollable queue container + each row
     so the highlighted item can be brought into view. */
  const queueScrollRef = useRef(null);
  const rowRefs = useRef({});

  /* Arrow keys move the queue selection. Ignored while the user is typing in a
     filter / lot-quantity input so we don't hijack the form. */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
      const target = e.target;
      const tag = target?.tagName;
      if (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        target?.isContentEditable
      )
        return;
      if (!queue.length) return;
      const idx = queue.findIndex((o) => o.orderId === selected);
      const next =
        e.key === "ArrowDown"
          ? Math.min(queue.length - 1, idx + 1)
          : Math.max(0, idx - 1);
      if (next === idx) return;
      e.preventDefault();
      setSelected(queue[next].orderId);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [queue, selected]);

  /* Keep the currently selected row visible inside the queue scroller. */
  useEffect(() => {
    const el = selected && rowRefs.current[selected];
    if (el?.scrollIntoView) el.scrollIntoView({ block: "nearest" });
  }, [selected]);


  /* Fetch batches per medicine line for the selected order — one
     v1/inventory/stock call per unique drug code. Results merge into `lots`,
     which ABLine filters by medId. */
  const currentLines = order ? orderLines[order.orderId] : null;
  useEffect(() => {
    if (!order || !currentLines || currentLines.length === 0) {
      setLots([]);
      return;
    }
    let alive = true;
    const codes = [
      ...new Set(currentLines.map((l) => l.drugCode).filter(Boolean)),
    ];
    Promise.all(
      codes.map((code) =>
        supplyService
          .getStockForMedicine(code, { warehouseId: whCode })
          .catch(() => [])
      )
    ).then((results) => {
      if (alive) setLots(results.flat());
    });
    return () => {
      alive = false;
    };
  }, [order?.orderId, currentLines, whCode]);

  /* Kick off the async pick-list CSV via the global task center so it keeps
     running (and shows progress in the topbar bell) even if the user navigates
     away. Mirrors the Out of Stuck report's task → poll → download flow. */
  const generatePickList = async (range) => {
    if (submitting) return;
    setSubmitting(true);
    setPickOpen(false);

    /* Same store the screen is working in — the pick list has to match what was
       allocated from it. */
    const warehouseId = whCode;
    if (!warehouseId) {
      toast.error("Select a store in the topbar first.");
      setSubmitting(false);
      return;
    }

    const { from, to } = toDateTimeRange(range);

    runTask({
      title: "In-Stock Pick List · CSV",
      run: async (onProgress) => {
        onProgress("Requesting…");
        const task = await pickListService.requestExport({ warehouseId, from, to });
        if (!task?.taskId) throw new Error("Pick list did not return a task id.");
        const fileName = task.fileName || `in-stock-pick-list-${warehouseId}.csv`;
        const doDownload = () => pickListService.download(task.taskId, fileName);

        let cur = task;
        for (let i = 0; cur.status === "PENDING" && i < POLL_MAX_ATTEMPTS; i++) {
          onProgress("Generating pick list…");
          await sleep(POLL_INTERVAL);
          cur = await pickListService.exportStatus(task.taskId);
        }
        if (cur.status === "FAILED") throw new Error(cur.message || "Pick list generation failed.");
        if (cur.status !== "READY") throw new Error("Pick list generation timed out. Please try again.");

        onProgress("Downloading…");
        await doDownload();
        return {
          detail: `Downloaded${cur.rowCount != null ? ` · ${cbFmtNum(cur.rowCount)} rows` : ""}`,
          action: { label: "Download again", run: doDownload },
        };
      },
    });
    toast.info("Generating pick list — track progress in notifications.");
    // brief guard against double-submit; the task itself lives in the task center
    setTimeout(() => setSubmitting(false), 1200);
  };

  return (
    <div
      className="cb-fade-in lg:h-[calc(100vh_-_var(--topbar-h)_-_var(--statusbar-h)_-_40px)]"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
    >
      <SectionHeader
        title="Allocation & Billing"
        sub={`${queue.length} Rx-verified order${queue.length !== 1 ? "s" : ""} ready for allocation`}
        actions={
          <Button
            variant="primary"
            icon={<IconDownload size={14} />}
            onClick={() => setPickOpen(true)}
            disabled={submitting}
          >
            Generate picklist
          </Button>
        }
      />

      <div
        style={{
          display: "flex",
          gap: 10,
          alignItems: "center",
          flexWrap: "wrap",
          padding: "10px 12px",
          border: "1px solid var(--border-default)",
          borderRadius: 6,
          background: "var(--bg-panel)",
        }}
      >
        <Select
          value={filters.orderType}
          onChange={(e) => {
            setPageNumber(1);
            setFilters((p) => ({
              ...p,
              orderType: e.target.value,
              eclinicCode: e.target.value === "B2C" ? "" : p.eclinicCode,
            }));
          }}
          style={{ minWidth: 140 }}
          aria-label="Order type"
        >
          <option value="CHE">CHE / Engage</option>
          <option value="B2C">B2C</option>
        </Select>

        <Input
          value={filters.orderId}
          onChange={(e) => setFilters((p) => ({ ...p, orderId: e.target.value }))}
          placeholder="Order ID"
          style={{ width: 200, maxWidth: "100%" }}
        />

        <Select
          value={sortOrder}
          onChange={(e) => {
            setPageNumber(1);
            setSortOrder(e.target.value);
          }}
          style={{ minWidth: 150 }}
          aria-label="Sort order"
          disabled={!!filters.orderId}
          title={filters.orderId ? "Sorting applies to the full queue, not an order-ID search" : "Sort the queue by date"}
        >
          <option value="DESC">Newest first</option>
          <option value="ASC">Oldest first</option>
        </Select>

        {filters.orderType === "CHE" && (
          <SearchableSelect
            value={filters.eclinicCode}
            onChange={(v) => {
              setPageNumber(1);
              setFilters((p) => ({ ...p, eclinicCode: v }));
            }}
            options={[
              { value: "", label: "Select eClinic" },
              ...eclinics.map((c) => ({ value: c.code, label: c.name })),
            ]}
            placeholder="Select eClinic"
            style={{ minWidth: 220 }}
            aria-label="eClinic"
          />
        )}

        {/* Order date + time window — dates and times picked in one popover.
            Applying commits all four fields at once, so the queue refetches once
            with the complete window (order-list fromDate/toDate + fromTime/toTime). */}
        <DateTimeRangeFilter
          label="Order date"
          from={filters.fromDate}
          to={filters.toDate}
          fromTime={filters.fromTime}
          toTime={filters.toTime}
          onApply={({ from, to, fromTime, toTime }) => {
            setPageNumber(1);
            setFilters((p) => ({
              ...p,
              fromDate: from,
              toDate: to,
              fromTime,
              toTime,
            }));
          }}
        />

        <Button
          variant="primary"
          icon={<IconSearch size={14} />}
          onClick={() => {
            setPageNumber(1);
            setReloadKey((k) => k + 1);
          }}
        >
          Search
        </Button>
        <Button onClick={onResetFilters}>Reset</Button>
      </div>

      {loading ? (
        <LoadingState label="Loading allocation queue…" />
      ) : error ? (
        <ErrorState message={error} onRetry={() => setReloadKey((k) => k + 1)} />
      ) : (
        <div
          className="grid-cols-1 lg:grid-cols-[300px_1fr] lg:grid-rows-[minmax(0,1fr)]"
          style={{
            display: "grid",
            gap: 0,
            flex: 1,
            minHeight: 0,
            border: "1px solid var(--border-default)",
            borderRadius: 6,
            overflow: "hidden",
            background: "var(--bg-panel)",
          }}
        >
          {/* Queue */}
          <div
            className="border-b lg:border-b-0 lg:border-r"
            style={{
              borderColor: "var(--border-default)",
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              height: "100%",
            }}
          >
            <div
              style={{
                padding: "10px 14px",
                borderBottom: "1px solid var(--border-subtle)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexShrink: 0,
              }}
            >
              <Eyebrow>Queue · page {pageNumber}</Eyebrow>
              <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--fg-muted)" }}>
                {queue.length}
              </span>
            </div>
            <div
              ref={queueScrollRef}
              className="cb-scroll max-h-[320px] lg:max-h-none"
              style={{
                overflowY: "auto",
                overflowX: "hidden",
                minHeight: 0,
                flex: 1,
              }}
            >
              {queue.length === 0 ? (
                <div style={{ padding: 24, textAlign: "center", color: "var(--fg-muted)", fontSize: 12, lineHeight: 1.6 }}>
                  {pageNumber > 1 ? (
                    <>
                      No more orders on this page.
                      <br />
                      Go back to the previous page.
                    </>
                  ) : (
                    <>
                      No Rx-verified orders waiting.
                      <br />
                      Verify prescriptions in <strong>Orders → Received</strong> to populate this queue.
                    </>
                  )}
                </div>
              ) : (
                queue.map((o) => (
                  <QueueRow
                    key={o.orderId}
                    order={o}
                    active={selected === o.orderId}
                    onClick={() => setSelected(o.orderId)}
                    rowRef={(el) => {
                      if (el) rowRefs.current[o.orderId] = el;
                      else delete rowRefs.current[o.orderId];
                    }}
                  />
                ))
              )}
            </div>
            <div
              style={{
                padding: "8px 10px",
                borderTop: "1px solid var(--border-subtle)",
                display: "flex",
                alignItems: "center",
                gap: 6,
                flexShrink: 0,
                background: "var(--bg-subtle)",
              }}
            >
              <Button
                onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
                disabled={loading || pageNumber <= 1}
                style={{ flex: 1, justifyContent: "center", height: 26 }}
              >
                Prev
              </Button>
              <span
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 11,
                  color: "var(--fg-muted)",
                  minWidth: 38,
                  textAlign: "center",
                }}
              >
                {pageNumber}
              </span>
              <Button
                onClick={() => setPageNumber((p) => p + 1)}
                disabled={loading || !hasNext}
                style={{ flex: 1, justifyContent: "center", height: 26 }}
              >
                Next
              </Button>
            </div>
          </div>

          {/* Workspace */}
          <div style={{ overflow: "hidden", display: "flex", flexDirection: "column", minHeight: 0, minWidth: 0 }}>
            {order ? (
              <ABWorkspace
                order={order}
                lots={lots}
                lines={orderLines[order.orderId]}
                whCode={whCode}
                onComplete={() => setReloadKey((k) => k + 1)}
                onLinesChanged={() =>
                  setOrderLines((prev) => {
                    const next = { ...prev };
                    delete next[order.orderId];
                    return next;
                  })
                }
              />
            ) : (
              <EmptyState icon={<IconPackage size={28} />} title="Select an order from the queue" />
            )}
          </div>
        </div>
      )}

      {pickOpen && (
        <PickListModal
          submitting={submitting}
          onCancel={() => setPickOpen(false)}
          onGenerate={generatePickList}
        />
      )}
    </div>
  );
};

/* Date-range popup for the async pick-list CSV. Seeds with the last 7 days and
   hands the chosen window to `onGenerate` as { fromDate, toDate, fromTime, toTime }. */
const PickListModal = ({ submitting, onCancel, onGenerate }) => {
  const [range, setRange] = useState(() => last7DaysRange());
  const hasRange = !!range.fromDate && !!range.toDate;
  const fmt = (d, t) => (d ? `${d}${t ? ` ${t}` : ""}` : "—");

  return (
    <div
      onClick={onCancel}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15,18,22,0.55)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      className="p-2 sm:p-6"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--bg-panel)",
          borderRadius: 8,
          width: 480,
          maxWidth: "100%",
          maxHeight: "90dvh",
          boxShadow: "0 24px 64px rgba(0,0,0,0.25)",
          overflowX: "hidden",
          overflowY: "auto",
        }}
      >
        <div style={{ padding: "14px 16px", borderBottom: "1px solid var(--border-default)" }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: "var(--fg-strong)" }}>Generate pick list</div>
          <div style={{ fontSize: 11, color: "var(--fg-muted)", marginTop: 2 }}>
            Pick a date &amp; time window — the CSV is generated in the background and appears in notifications.
          </div>
        </div>

        <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 14 }}>
          <DateTimeRangeFilter
            label="Date range"
            from={range.fromDate}
            to={range.toDate}
            fromTime={range.fromTime}
            toTime={range.toTime}
            onApply={({ from, to, fromTime, toTime }) =>
              setRange({ fromDate: from, toDate: to, fromTime, toTime })
            }
          />
          <div style={{ fontSize: 12, color: "var(--fg-default)" }}>
            <span style={{ color: "var(--fg-muted)" }}>Selected: </span>
            <span style={{ fontFamily: "var(--font-mono)" }}>
              {fmt(range.fromDate, range.fromTime)} → {fmt(range.toDate, range.toTime)}
            </span>
          </div>
        </div>

        <div
          style={{
            padding: "12px 16px",
            borderTop: "1px solid var(--border-default)",
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
          }}
        >
          <Button onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            icon={<IconDownload size={14} />}
            onClick={() => onGenerate(range)}
            disabled={submitting || !hasRange}
          >
            Generate
          </Button>
        </div>
      </div>
    </div>
  );
};


const altModeBtn = (active, side) => ({
  padding: "5px 12px",
  fontSize: 12,
  fontWeight: 600,
  cursor: "pointer",
  border: `1px solid ${active ? "var(--ink-600)" : "var(--border-default)"}`,
  borderLeftWidth: side === "right" ? 0 : 1,
  borderRadius: side === "left" ? "4px 0 0 4px" : "0 4px 4px 0",
  /* Active side matches the primary (Confirm) button. */
  background: active ? "var(--ink-600)" : "var(--bg-panel)",
  color: active ? "var(--fg-on-ink)" : "var(--fg-default)",
});

const fromMedicineIdRow = (m) => ({
  medId: m.id,
  medicineName: m.medicineName,
  mrp: Number(m.medicineRate ?? m.unitPrice) || 0,
  unitPack: m.medicineTypeOfSell || "",
  strips: Number(m.stock) || 0,
});

const AlternativeModal = ({ line, whCode, initial, onCancel, onConfirm }) => {
  const [mode, setMode] = useState("name");
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(initial || null);
  const [saving, setSaving] = useState(false);
  const minLen = mode === "id" ? 4 : 3;

  useEffect(() => {
    const trimmed = q.trim();
    if (trimmed.length < minLen) {
      setResults([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    let alive = true;
    const search =
      mode === "id"
        ? () =>
          supplyService.getMedicineById(trimmed.toUpperCase()).then((rows) => {
            const items = rows.map(fromMedicineIdRow);
            const exact = String(trimmed).toUpperCase();
            return [
              ...items.filter((m) => String(m.medId).toUpperCase() === exact),
              ...items.filter((m) => String(m.medId).toUpperCase() !== exact),
            ];
          })
        : () => supplyService.searchInventoryProducts(trimmed, { warehouseId: whCode });
    const handle = setTimeout(() => {
      search()
        .then((items) => {
          if (!alive) return;
          setResults((items || []).filter((m) => String(m.medId) !== String(line.drugCode)));
          setLoading(false);
        })
        .catch(() => {
          if (!alive) return;
          setResults([]);
          setLoading(false);
        });
    }, 250);
    return () => {
      alive = false;
      clearTimeout(handle);
    };
  }, [q, mode, minLen, whCode, line.drugCode]);

  const switchMode = (m) => {
    if (m === mode) return;
    setMode(m);
    setQ("");
    setResults([]);
    setSelected(null);
  };

  const confirm = async () => {
    if (!selected || saving) return;
    setSaving(true);
    try {
      await onConfirm(selected);
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && !saving && onCancel();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel, saving]);

  const hint =
    q.trim().length < minLen
      ? mode === "id"
        ? "Enter a medicine ID, e.g. CB95311."
        : "Type at least 3 characters to search."
      : loading
        ? "Searching…"
        : results.length
          ? null
          : mode === "id"
            ? "No medicine found for this ID."
            : "No in-stock medicines match.";

  return (
    <div
      onClick={() => !saving && onCancel()}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15,18,22,0.55)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      className="p-2 sm:p-6"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--bg-panel)",
          borderRadius: 8,
          width: 560,
          maxWidth: "100%",
          maxHeight: "90dvh",
          boxShadow: "0 24px 64px rgba(0,0,0,0.25)",
          overflowX: "hidden",
          overflowY: "auto",
        }}
      >
        <div style={{ padding: "14px 16px", borderBottom: "1px solid var(--border-default)" }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: "var(--fg-strong)" }}>Choose an alternative</div>
          <div style={{ fontSize: 11, color: "var(--fg-muted)", marginTop: 2 }}>
            For <strong>{line.drugName}</strong>{" "}
            <span style={{ fontFamily: "var(--font-mono)" }}>{line.drugCode}</span> · ordered {line.qty}
          </div>
        </div>

        <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ display: "flex", alignSelf: "flex-start" }}>
            <button type="button" onClick={() => switchMode("name")} style={altModeBtn(mode === "name", "left")}>
              Medicine name
            </button>
            <button type="button" onClick={() => switchMode("id")} style={altModeBtn(mode === "id", "right")}>
              Medicine ID
            </button>
          </div>
          <Input
            key={mode}
            autoFocus
            mono={mode === "id"}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={mode === "id" ? "Enter medicine ID (e.g. CB95311)…" : "Search medicine name…"}
            prefix={<IconSearch size={14} />}
          />
          <div
            className="cb-scroll"
            style={{
              maxHeight: 280,
              overflowY: "auto",
              border: "1px solid var(--border-subtle)",
              borderRadius: 4,
            }}
          >
            {hint ? (
              <div style={{ padding: 14, fontSize: 12, color: "var(--fg-muted)", textAlign: "center" }}>{hint}</div>
            ) : (
              results.map((m) => {
                const isSel = selected && String(selected.medId) === String(m.medId);
                return (
                  <div
                    key={m.medId}
                    onClick={() => setSelected(m)}
                    style={{
                      padding: "8px 12px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      borderBottom: "1px solid var(--border-subtle)",
                      background: isSel ? "var(--bg-subtle)" : "transparent",
                    }}
                  >
                    <input type="radio" readOnly checked={!!isSel} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: "var(--fg-strong)" }}>{m.medicineName}</div>
                      <div style={{ fontSize: 11, color: "var(--fg-muted)", fontFamily: "var(--font-mono)" }}>
                        {m.medId}
                        {m.unitPack ? ` · ${m.unitPack}` : ""}
                      </div>
                    </div>
                    <div style={{ textAlign: "right", fontSize: 11, color: "var(--fg-default)" }}>
                      <div>MRP {cbFmtINR(m.mrp)}</div>
                      <div style={{ color: "var(--fg-muted)" }}>{cbFmtNum(m.strips)} in stock</div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        <div
          style={{
            padding: "12px 16px",
            borderTop: "1px solid var(--border-default)",
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
          }}
        >
          <Button onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" icon={<IconCheck size={14} />} onClick={confirm} disabled={!selected || saving}>
            {saving ? "Substituting…" : "Confirm"}
          </Button>
        </div>
      </div>
    </div>
  );
};

const QueueRow = ({ order, active, onClick, rowRef }) => (
  <button
    ref={rowRef}
    onClick={onClick}
    style={{
      display: "block",
      width: "100%",
      textAlign: "left",
      padding: "10px 14px",
      border: 0,
      borderBottom: "1px solid var(--border-subtle)",
      background: active ? "var(--bg-selected)" : "transparent",
      boxShadow: active ? "inset 2px 0 0 var(--ink-600)" : "none",
      cursor: "pointer",
    }}
  >
    <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8 }}>
      <span style={{ fontFamily: "var(--font-mono)", fontSize: 12, fontWeight: 700, color: "var(--fg-strong)" }}>
        {order.orderId}
      </span>
      <Pill variant="green" dot style={{ height: 14, fontSize: 8, padding: "0 5px" }}>
        Rx ✓
      </Pill>
    </div>
    <div style={{ fontSize: 12, fontWeight: 600, color: "var(--fg-default)", marginTop: 4 }}>
      {order.patientName}
    </div>
    <div style={{ fontSize: 10, color: "var(--fg-muted)", marginTop: 3, fontFamily: "var(--font-mono)" }}>
      {order.modifiedDate} · {cbFmtINR(order.paidAmount)}
    </div>
  </button>
);

/* ---------------- Workspace ---------------- */
const ABWorkspace = ({ order, lots, lines: rawLines, whCode, onComplete, onLinesChanged }) => {
  const lines = rawLines || [];

  const [alternatives, setAlternatives] = useState({});
  const [altLine, setAltLine] = useState(null);
  const patientAddr = [order.address1, order.address2, order.address3].filter(Boolean).join(", ");
  const eclinicAddr = [order.deliveryAddress1, order.deliveryAddress2, order.deliveryAddress3]
    .filter(Boolean)
    .join(", ");

  const [mode, setMode] = useState("manual"); // manual | auto
  const [alloc, setAlloc] = useState({});
  const [posting, setPosting] = useState(false);
  const [postErr, setPostErr] = useState(null);
  const [invoicePreview, setInvoicePreview] = useState(null);
  const [printingInvoice, setPrintingInvoice] = useState(false);

  /* Lock the workspace when the order is already past the allocation step.
     statusDesc comes from order-list and is the operator-facing string for
     the current procedure stage. "Allocated" means inventory is reserved;
     "Invoiced" means a sales invoice was already generated. */
  const orderLocked = ["Allocated", "Invoiced"].includes(order.statusDesc);

  /* Stable per-line key — `alloc` was previously keyed by drugCode, which
     broke whenever the same medicine appeared on multiple order lines
     (Auto FIFO overwrote one line's picks with the next, and the same lot
     unit got double-counted across lines). Keying by orderDetailsId
     (with a positional fallback) keeps each line independent. */
  const lineKeyOf = (l, idx) =>
    String(l.id ?? l.orderDetailsId ?? `${l.drugCode}_${idx}`);

  /* Reset allocation map whenever the order or its line set changes. */
  useEffect(() => {
    const init = {};
    (rawLines || []).forEach((l, idx) => {
      init[lineKeyOf(l, idx)] = {};
    });
    setAlloc(init);
    setMode("manual");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order.orderId, rawLines]);

  /* Auto FIFO when the mode flips to auto. Lines that the backend has
     already allocated or invoiced are skipped — they are read-only.
     `remByLot` tracks lot availability across all lines so the same unit
     is never allocated twice when several lines share a medicine. */
  useEffect(() => {
    if (mode !== "auto") return;
    const next = {};
    const remByLot = {};
    lots.forEach((L) => {
      remByLot[L.id] = Number(L.qtyA) || 0;
    });
    (rawLines || []).forEach((l, idx) => {
      const key = lineKeyOf(l, idx);
      const desc = String(l.statusDesc || "");
      if (
        String(l.statusCode) === "2020" ||
        desc === "Allocated" ||
        desc === "Invoiced"
      ) {
        next[key] = {};
        return;
      }
      const qty = Number(l.quantity) || 0;
      const cands = lots
        .filter((L) => L.medId === l.drugCode && (remByLot[L.id] || 0) > 0)
        .sort((a, b) => (a.exp || "").localeCompare(b.exp || "") || (a.date || "").localeCompare(b.date || ""));
      const picks = {};
      let rem = qty;
      for (const lot of cands) {
        if (rem <= 0) break;
        const avail = remByLot[lot.id] || 0;
        const take = Math.min(avail, rem);
        if (take <= 0) continue;
        picks[lot.id] = take;
        rem -= take;
        remByLot[lot.id] -= take;
      }
      next[key] = picks;
    });
    setAlloc(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, rawLines, lots]);

  const setLot = (lineKey, lotId, qty) =>
    setAlloc((p) => ({ ...p, [lineKey]: { ...(p[lineKey] || {}), [lotId]: Math.max(0, Number(qty) || 0) } }));

  const lineStatus = lines.map((l, idx) => {
    const qty = Number(l.quantity) || 0;
    const key = lineKeyOf(l, idx);
    const picks = alloc[key] || {};
    const allocated = Object.values(picks).reduce((s, q) => s + (Number(q) || 0), 0);
    /* A line is locked when the backend has already moved it past allocation.
       statusCode 2020 = "Allocated"; "Invoiced" lines also come back with
       statusDesc set but a different code, so we accept either signal. */
    const desc = String(l.statusDesc || "");
    const isAllocated =
      String(l.statusCode) === "2020" || desc === "Allocated" || desc === "Invoiced";
    return {
      ...l,
      lineKey: key,
      qty,
      allocated,
      isAllocated,
      lockedLabel: desc || (isAllocated ? "Allocated" : ""),
      complete: isAllocated || (qty > 0 && allocated === qty),
      partial: !isAllocated && allocated > 0 && allocated < qty,
      /* Over-allocation: more units picked than ordered. Not a valid bill —
         the operator must reduce the allocation before posting. */
      excess: !isAllocated && qty > 0 && allocated > qty,
    };
  });

  const anyAllocated = lineStatus.some((l) => l.allocated > 0);
  const allComplete = lineStatus.every((l) => l.complete);
  const hasExcess = lineStatus.some((l) => l.excess);
  const isPartialBill = anyAllocated && !allComplete && !hasExcess;

  /* Selling price (and MRP) is tax-inclusive, and GST is per-batch (lot.gstPct)
     — so GST is extracted from the line amount, not added on top, exactly like
     the printed invoice (printInvoice.js computeInvoiceMath). Total therefore
     equals the inclusive line amount, not amount + tax. */
  let total = 0; // inclusive line amount (= invoice Net before round-off)
  let taxableValue = 0;
  let cgst = 0;
  let sgst = 0;
  lineStatus.forEach((l) => {
    const picks = alloc[l.lineKey] || {};
    Object.entries(picks).forEach(([lotId, qty]) => {
      const lot = lots.find((L) => L.id === Number(lotId));
      if (!lot) return;
      const net = lot.sale * qty;
      const rate = Number(lot.gstPct) || 0;
      const taxable = net / (1 + rate / 100);
      const halfTax = (net - taxable) / 2;
      total += net;
      taxableValue += taxable;
      cgst += halfTax;
      sgst += halfTax;
    });
  });

  const handlePost = () => {
    if (hasExcess) {
      setPostErr("Excess quantity selected — reduce the allocation to the ordered quantity before billing.");
      return;
    }
    setPosting(true);
    setPostErr(null);

    /* Build the invoice once — same artifact drives the preview modal,
       the v1/sales create body, the v1/sales update body fired on Print,
       and the actual print window. */
    const invoice = buildInvoice({
      order,
      lineStatus,
      alloc,
      lots,
      totals: { net: total, partial: isPartialBill },
      warehouseId: whCode,
    });
    if (!invoice) {
      setPosting(false);
      setPostErr("Nothing allocated to bill.");
      return;
    }

    supplyService
      .postSalesInvoice({ data: invoice.updatePayload })
      .then((res) => {
        setPosting(false);
        /* The backend returns the freshly created invoice. We capture its
           invoiceId (used by the document upload on Print) and re-render the
           preview HTML using the backend's invoiceNumber + invoiceDate so the
           operator sees the real bill no, not the client-generated stub. */
        const created = res?.data ?? res ?? {};
        const invoiceId = created.invoiceId ?? created.id ?? null;
        /* Render the invoice the backend just created, NOT the local preview.
           The backend applies the e-clinic / scheme discount that the client-side
           build doesn't compute, so `created` carries the authoritative discount /
           GST / net payable. The create response omits per-item gstPercentage and
           patient age/gender, so enrich those from the order lines before mapping,
           then render via buildInvoiceFromSales. Fall back to the local build if
           the response can't be rendered (e.g. no items). */
        const gstByCode = {};
        (lineStatus || []).forEach((l) => {
          if (l?.drugCode) gstByCode[l.drugCode] = Number(l.gstPct ?? l.taxPct) || 5;
        });
        const isB2C = order?.orderSource === "B2C";
        const enriched = {
          ...created,
          paymentMode: created.paymentMode || (isB2C ? "B2C (Online)" : "E-Clinic (Online)"),
          patientId: order?.patientId || order?.patientCode || null,
          patientAge: order?.patientAge || order?.age || null,
          patientGender: order?.patientGender || order?.gender || null,
          items: Array.isArray(created.items)
            ? created.items.map((it) => {
              const g = Number(it.gstPercentage);
              return { ...it, gstPercentage: g > 0 ? g : gstByCode[it.medicineCode] ?? 5 };
            })
            : created.items,
        };
        const mapped = supplyService.normalizeSalesInvoice(enriched);
        const fromSales = mapped ? buildInvoiceFromSales(mapped) : null;
        const finalInvoice = fromSales || invoice;
        setInvoicePreview({ ...finalInvoice, invoiceId });
      })
      .catch((e) => {
        setPosting(false);
        setPostErr(
          e?.response?.data?.message ||
          e?.message ||
          "Could not post the bill. Please try again."
        );
      });
  };

  /* Print button inside the preview: render the preview HTML to a PDF blob,
     upload it to v1/sales/{id}/document, then open the print window. The PDF
     blob is the same artifact the operator sees in the preview iframe. */
  const handlePreviewPrint = () => {
    if (!invoicePreview) return;
    setPrintingInvoice(true);
    /* Same split as Bill Entry: the order is already billed, so a failed
       attachment warns and still prints. */
    const attach =
      invoicePreview.invoiceId != null
        ? invoiceToPdfBlob(invoicePreview).then((file) =>
          supplyService
            .uploadSalesDocument(invoicePreview.invoiceId, file)
            .catch((e) => {
              toast.warn(describeDocumentUploadError(e, file));
            })
        )
        : Promise.resolve(null);
    attach
      .then(() => {
        openPrintWindow(invoicePreview);
        setPrintingInvoice(false);
        setInvoicePreview(null);
        onComplete();
      })
      .catch((e) => {
        /* Only a render failure reaches here — there is nothing to print. */
        setPrintingInvoice(false);
        setPostErr(e?.message || "Could not render the invoice for printing.");
      });
  };

  const handlePreviewClose = () => {
    setInvoicePreview(null);
    onComplete();
  };

  return (
    <>
      <div
        style={{
          padding: "14px 20px",
          background: "var(--bg-panel)",
          borderBottom: "1px solid var(--border-default)",
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 14,
          flexWrap: "wrap",
        }}
      >
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <h3 style={{ margin: 0, fontSize: 18, color: "var(--fg-strong)" }}>{order.orderId}</h3>
            <Pill variant="green" dot>
              Rx verified
            </Pill>
            <Pill variant="ink">{order?.orderSource === "B2C" ? "B2C" : "CHE"}</Pill>
          </div>
          <div style={{ fontSize: 13, color: "var(--fg-default)", marginTop: 6, fontWeight: 600 }}>
            {order.patientName}
            <span
              style={{
                fontWeight: 400,
                color: "var(--fg-muted)",
                marginLeft: 10,
                fontFamily: "var(--font-mono)",
                fontSize: 11,
              }}
            >
              {order.patientId} · {order.patientMobile}
            </span>
          </div>
          {order.cheBranchName && (
            <div style={{ fontSize: 11, color: "var(--fg-muted)", marginTop: 4 }}>
              via <strong>{order.cheBranchName}</strong>
              {eclinicAddr && <> · {eclinicAddr}</>}
            </div>
          )}
          {patientAddr && (
            <div style={{ fontSize: 11, color: "var(--fg-muted)", marginTop: 2 }}>
              Ship to: {patientAddr}
            </div>
          )}
        </div>
        {orderLocked ? (
          <Pill variant="green" dot>
            {order.statusDesc}
          </Pill>
        ) : (
          <div style={{ display: "flex", gap: 0, border: "1px solid var(--border-default)", borderRadius: 4, overflow: "hidden" }}>
            <button onClick={() => setMode("manual")} style={modeBtn(mode === "manual")}>
              <IconUser size={11} /> Manual
            </button>
            <button onClick={() => setMode("auto")} style={modeBtn(mode === "auto")}>
              <IconLock size={11} /> Auto FIFO
            </button>
          </div>
        )}
      </div>

      <div className="cb-scroll" style={{ padding: "10px 20px", background: "var(--bg-canvas)", borderBottom: "1px solid var(--border-subtle)", overflowX: "auto" }}>
        <ABPipeline partial={isPartialBill} />
      </div>

      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          padding: "14px 20px",
          display: "flex",
          flexDirection: "column",
          gap: 12,
        }}
        className="cb-scroll"
      >
        {lineStatus.map((line) => (
          <ABLine
            key={line.lineKey}
            line={line}
            lots={lots}
            alloc={alloc[line.lineKey] || {}}
            mode={mode}
            onSet={(lotId, qty) => setLot(line.lineKey, lotId, qty)}
            inputsDisabled={orderLocked}
            alternative={alternatives[line.lineKey]}
            onAlternative={() => setAltLine(line)}
          />
        ))}

        {anyAllocated && (
          <div
            style={{
              marginTop: 6,
              padding: "14px 16px",
              background: hasExcess ? "var(--red-50)" : isPartialBill ? "var(--amber-50)" : "var(--green-50)",
              border: `1px solid ${hasExcess ? "var(--red-100)" : isPartialBill ? "var(--amber-100)" : "var(--green-100)"
                }`,
              borderRadius: 6,
              display: "grid",
              gap: 18,
              flexShrink: 0,
            }}
            className="grid-cols-1 sm:grid-cols-[1fr_240px]"
          >
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <Eyebrow
                  style={{
                    color: hasExcess ? "var(--red-700)" : isPartialBill ? "var(--amber-700)" : "var(--green-700)",
                  }}
                >
                  Invoice preview
                </Eyebrow>
                {hasExcess ? (
                  <Pill variant="red" dot>
                    Excess quantity
                  </Pill>
                ) : isPartialBill ? (
                  <Pill variant="amber" dot>
                    Partial bill
                  </Pill>
                ) : (
                  <Pill variant="green" dot>
                    Full bill
                  </Pill>
                )}
              </div>
              <div style={{ fontSize: 12, color: "var(--fg-default)", lineHeight: 1.6 }}>
                {hasExcess ? (
                  <>
                    <strong>Excess quantity selected</strong> on{" "}
                    {lineStatus.filter((l) => l.excess).map((l) => l.drugName).join(", ")} — allocated{" "}
                    {lineStatus.filter((l) => l.excess).reduce((s, l) => s + l.allocated, 0)} units against{" "}
                    {lineStatus.filter((l) => l.excess).reduce((s, l) => s + l.qty, 0)} ordered. Reduce the allocation
                    to the ordered quantity to bill.
                  </>
                ) : isPartialBill ? (
                  <>
                    Billing <strong>{lineStatus.filter((l) => l.allocated > 0).length} of {lines.length}</strong> lines (
                    {lineStatus.reduce((s, l) => s + l.allocated, 0)} of {lineStatus.reduce((s, l) => s + l.qty, 0)}{" "}
                    units). Remaining qty will be flagged as <strong>shortage</strong> — operator can back-order or
                    cancel.
                  </>
                ) : (
                  <>
                    All <strong>{lines.length} lines</strong> fully allocated (
                    {lineStatus.reduce((s, l) => s + l.qty, 0)} units). Invoice will be generated and the order moved
                    to <strong>Confirm orders</strong>.
                  </>
                )}
              </div>
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 4,
                fontSize: 12,
                padding: "10px 12px",
                background: "var(--bg-panel)",
                borderRadius: 4,
                border: "1px solid var(--border-subtle)",
              }}
            >
              <SumRow label="Taxable value" value={cbFmtINR(taxableValue)} />
              <SumRow label="CGST" value={cbFmtINR(cgst)} muted />
              <SumRow label="SGST" value={cbFmtINR(sgst)} muted />
              <div style={{ height: 1, background: "var(--border-subtle)", margin: "4px 0" }} />
              <SumRow label="Total" value={cbFmtINR(total)} bold />
            </div>
          </div>
        )}
      </div>

      {postErr && (
        <div style={{ padding: "10px 20px 0" }}>
          <InlineAlert>{postErr}</InlineAlert>
        </div>
      )}

      <div
        style={{
          padding: "12px 20px",
          background: "var(--bg-panel)",
          borderTop: "1px solid var(--border-default)",
          display: "flex",
          gap: 10,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <span style={{ fontSize: 11, color: "var(--fg-muted)", fontFamily: "var(--font-mono)" }}>
          {lineStatus.filter((l) => l.complete).length} of {lines.length} lines full ·{" "}
          {lineStatus.filter((l) => l.partial).length} partial
        </span>
        <div style={{ flex: 1 }} />
        <Button disabled={posting || orderLocked}>Save draft</Button>
        <Button disabled={posting || orderLocked}>Cancel order</Button>
        <Button
          variant="primary"
          disabled={!anyAllocated || hasExcess || posting || orderLocked}
          icon={<IconCheck size={14} />}
          onClick={handlePost}
          style={{ height: "auto", minHeight: 30, maxWidth: "100%" }}
        >
          <span className="whitespace-normal sm:whitespace-nowrap py-1 sm:py-0">
            {orderLocked
              ? `Order already ${order.statusDesc}`
              : hasExcess
                ? "Reduce excess to bill"
                : posting
                  ? "Posting…"
                  : `${isPartialBill ? "Post partial bill" : "Post full bill"} · ${cbFmtINR(total)} · move to Confirm`}
          </span>
        </Button>
      </div>

      {altLine && (
        <AlternativeModal
          line={altLine}
          whCode={whCode}
          initial={alternatives[altLine.lineKey]}
          onCancel={() => setAltLine(null)}
          onConfirm={async (med) => {
            const line = altLine;
            try {

              const r = await apiService.getOrderDetails({
                patientDrugPrescriptionOrderId: order.orderId,
                status: 1,
                ...(whCode ? { hospitalId: whCode } : {}),
              });
              const rows = r?.data?.successList || [];
              const row =
                rows.find((x) => line.id != null && String(x.id) === String(line.id)) ||
                rows.find((x) => String(x.drugCode) === String(line.drugCode));
              const lineId = row?.id ?? line.id;
              if (lineId == null) throw new Error("Could not find this order line.");
              await supplyService.substituteOrderLine({ lineId, substituteDrugCode: med.medId });
              setAlternatives((prev) => ({ ...prev, [line.lineKey]: med }));
              toast.success(`${line.drugName} substituted with ${med.medicineName}.`);
              setAltLine(null);
              onLinesChanged && onLinesChanged();
            } catch (e) {
              toast.error(e?.response?.data?.message || e?.message || "Could not substitute this line.");
            }
          }}
        />
      )}
      {invoicePreview && (
        <InvoicePreviewModal
          invoice={invoicePreview}
          printing={printingInvoice}
          onPrint={handlePreviewPrint}
          onClose={handlePreviewClose}
        />
      )}
    </>
  );
};

/* In-app preview of the just-posted invoice. The HTML body is the same one
   that will go to the print window — rendered inside an iframe so its <style>
   block is scoped and can't bleed into the surrounding app. Print renders the
   same HTML to a PDF blob (html2pdf) and uploads it to v1/sales/{id}/document
   before opening the print window. */
const InvoicePreviewModal = ({ invoice, printing, onPrint, onClose }) => {
  const srcDoc = `<!DOCTYPE html><html><head><meta charset="utf-8"/><style>${invoice.styles}</style></head><body>${invoice.html}</body></html>`;
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15,18,22,0.55)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      className="p-2 sm:p-6"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--bg-panel)",
          borderRadius: 8,
          maxWidth: 980,
          width: "100%",
          height: "92vh",
          maxHeight: "92dvh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 24px 64px rgba(0,0,0,0.25)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            padding: "12px 16px",
            borderBottom: "1px solid var(--border-default)",
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: "var(--fg-strong)" }}>
              Invoice preview · {invoice.billNo}
            </div>
            <div style={{ fontSize: 11, color: "var(--fg-muted)", marginTop: 2 }}>
              Bill posted to backend. Click Print to upload the PDF and finalize.
            </div>
          </div>
          <Button onClick={onClose} disabled={printing}>
            Close
          </Button>
          <Button
            variant="primary"
            icon={<IconCheck size={14} />}
            onClick={onPrint}
            disabled={printing}
          >
            {printing ? "Uploading…" : "Print"}
          </Button>
        </div>
        <iframe
          title={`Invoice ${invoice.billNo}`}
          srcDoc={srcDoc}
          style={{
            border: 0,
            width: "100%",
            flex: 1,
            background: "#fff",
          }}
        />
      </div>
    </div>
  );
};

const modeBtn = (active) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  padding: "0 10px",
  height: 28,
  border: 0,
  cursor: "pointer",
  background: active ? "var(--ink-50)" : "var(--bg-panel)",
  color: active ? "var(--ink-700)" : "var(--fg-default)",
  fontFamily: "var(--font-sans)",
  fontSize: 11,
  fontWeight: 600,
  borderRight: "1px solid var(--border-default)",
});

const SumRow = ({ label, value, muted, bold }) => (
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      gap: 8,
      fontWeight: bold ? 700 : 500,
      color: bold ? "var(--fg-strong)" : muted ? "var(--fg-muted)" : "var(--fg-default)",
      fontSize: bold ? 14 : 12,
    }}
  >
    <span style={{ whiteSpace: "nowrap" }}>{label}</span>
    <span style={{ fontFamily: "var(--font-mono)" }}>{value}</span>
  </div>
);

/* ---------------- Line card ---------------- */
const ABLine = ({ line, lots, alloc, mode, onSet, inputsDisabled, alternative, onAlternative }) => {
  const cands = lots
    .filter((l) => l.medId === line.drugCode && l.qtyA > 0)
    .sort((a, b) => (a.exp || "").localeCompare(b.exp || "") || (a.date || "").localeCompare(b.date || ""));
  const pct = Math.round((line.allocated / line.qty) * 100);
  const cols = "40px 1fr 110px 90px 110px 110px";

  return (
    <div
      style={{
        background: "var(--bg-panel)",
        border: `1px solid ${line.excess
          ? "var(--red-300)"
          : line.complete
            ? "var(--green-300)"
            : line.partial
              ? "var(--amber-300)"
              : "var(--border-default)"
          }`,
        borderRadius: 6,
        overflow: "hidden",
        flexShrink: 0,
      }}
    >
      <div
        style={{
          padding: "10px 14px",
          background: line.excess
            ? "var(--red-50)"
            : line.isAllocated || line.complete
              ? "var(--green-50)"
              : line.partial
                ? "var(--amber-50)"
                : "var(--bg-subtle)",
          borderBottom: line.isAllocated ? 0 : "1px solid var(--border-subtle)",
          display: "flex",
          alignItems: "center",
          gap: 14,
          flexWrap: "wrap",
        }}
      >
        <div className="flex-[1_1_100%] sm:flex-1" style={{ minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
            <span style={{ fontWeight: 700, fontSize: 13, color: "var(--fg-strong)" }}>{line.drugName}</span>
            <span style={{ fontSize: 11, color: "var(--fg-muted)", fontFamily: "var(--font-mono)" }}>{line.drugCode}</span>


            {!line.isAllocated && !inputsDisabled && (
              <Button size="sm" icon={<IconSearch size={12} />} onClick={onAlternative}>
                Alternative
              </Button>
            )}
          </div>
          {alternative && (
            <div style={{ fontSize: 11, color: "var(--fg-default)", marginTop: 4 }}>
              <span style={{ color: "var(--fg-muted)" }}>Alternative: </span>
              <span style={{ fontWeight: 600 }}>{alternative.medicineName}</span>{" "}
              <span style={{ fontFamily: "var(--font-mono)", color: "var(--fg-muted)" }}>{alternative.medId}</span>
            </div>
          )}
        </div>
        <ABStat label="Ordered" value={line.qty} />
        <ABStat
          label="Allocated"
          value={
            line.isAllocated ? (
              line.qty
            ) : (
              <>
                {line.allocated}
                <span style={{ fontSize: 11, color: "var(--fg-muted)", fontWeight: 400 }}> ({pct || 0}%)</span>
              </>
            )
          }
          color={
            line.excess
              ? "var(--red-700)"
              : line.isAllocated || line.complete
                ? "var(--green-700)"
                : line.partial
                  ? "var(--amber-700)"
                  : "var(--fg-default)"
          }
        />
        <div style={{ minWidth: 110, textAlign: "right" }}>
          {line.isAllocated ? (
            <Pill variant="green" dot>
              {line.lockedLabel || "Allocated"}
            </Pill>
          ) : line.excess ? (
            <Pill variant="red" dot>
              Excess
            </Pill>
          ) : line.complete ? (
            <Pill variant="green" dot>
              Full
            </Pill>
          ) : line.partial ? (
            <Pill variant="amber" dot>
              Partial
            </Pill>
          ) : (
            <Pill variant="neutral">Pending</Pill>
          )}
        </div>
      </div>

      <div className="cb-scroll" style={{ overflowX: "auto" }}>
      {!line.isAllocated && (
        <div
          className="min-w-[520px] lg:min-w-0"
          style={{
            display: "grid",
            gridTemplateColumns: cols,
            padding: "6px 14px",
            background: "var(--bg-canvas)",
            borderBottom: "1px solid var(--border-subtle)",
            fontSize: 9,
            fontWeight: 600,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: "var(--fg-muted)",
            gap: 8,
            alignItems: "center",
          }}
        >
          <span>FIFO</span>
          <span>Lot · batch</span>
          <span>Expiry</span>
          <span style={{ textAlign: "right" }}>Sale ₹</span>
          <span style={{ textAlign: "right" }}>Available</span>
          <span style={{ textAlign: "right" }}>Allocate</span>
        </div>
      )}
      {!line.isAllocated && cands.length === 0 && (
        <div style={{ padding: 14, textAlign: "center", color: "var(--red-700)", fontSize: 12, background: "var(--red-50)" }}>
          <IconAlert size={13} style={{ verticalAlign: "-2px", marginRight: 6 }} />
          No lots available — partial bill required, or cancel this line
        </div>
      )}
      {!line.isAllocated && cands.map((lot, i) => {
        const taken = alloc[lot.id] || 0;
        const exp = cbExpiryStatus(lot.exp);
        return (
          <div
            key={lot.id}
            className="min-w-[520px] lg:min-w-0"
            style={{
              display: "grid",
              gridTemplateColumns: cols,
              padding: "8px 14px",
              gap: 8,
              alignItems: "center",
              borderBottom: "1px solid var(--border-subtle)",
              background: taken > 0 ? "var(--green-50)" : "transparent",
            }}
          >
            <span
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 11,
                fontWeight: 700,
                color: i === 0 ? "var(--ink-600)" : "var(--fg-muted)",
              }}
            >
              {i + 1}
              {i === 0 && "↑"}
            </span>
            <div>
              <div style={{ fontSize: 12, fontFamily: "var(--font-mono)", fontWeight: 600, color: "var(--fg-strong)" }}>
                Lot {lot.id} · {lot.batch}
              </div>
              <div style={{ fontSize: 10, color: "var(--fg-muted)", fontFamily: "var(--font-mono)", marginTop: 2 }}>
                {lot.grn}
              </div>
            </div>
            <span
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 12,
                color:
                  exp.status === "expired"
                    ? "var(--red-700)"
                    : exp.status === "soon"
                      ? "var(--amber-700)"
                      : "var(--fg-default)",
              }}
            >
              {lot.exp}
            </span>
            <span style={{ textAlign: "right", fontFamily: "var(--font-mono)", fontSize: 12 }}>
              {cbFmtINR(lot.sale)}
            </span>
            <span style={{ textAlign: "right", fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--fg-muted)" }}>
              {cbFmtNum(lot.qtyA)}
            </span>
            <div style={{ textAlign: "right" }}>
              {mode === "manual" && !line.isAllocated && !inputsDisabled ? (
                <input
                  type="number"
                  min={0}
                  max={lot.qtyA}
                  value={taken}
                  onChange={(e) => onSet(lot.id, Math.min(lot.qtyA, parseInt(e.target.value, 10) || 0))}
                  style={{
                    width: 80,
                    height: 26,
                    padding: "0 6px",
                    border: line.excess
                      ? "1px solid var(--red-500)"
                      : taken > 0
                        ? "1px solid var(--green-500)"
                        : "1px solid var(--border-default)",
                    borderRadius: 3,
                    fontFamily: "var(--font-mono)",
                    fontSize: 13,
                    fontWeight: 600,
                    textAlign: "right",
                    background: "var(--bg-panel)",
                    outline: "none",
                  }}
                />
              ) : (
                <span
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: 13,
                    fontWeight: 700,
                    color: taken > 0 ? "var(--ink-700)" : "var(--fg-subtle)",
                  }}
                >
                  {taken || "—"}
                </span>
              )}
            </div>
          </div>
        );
      })}
      </div>
    </div>
  );
};

const ABStat = ({ label, value, color }) => (
  <div style={{ textAlign: "right" }}>
    <div style={{ fontSize: 9, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--fg-muted)" }}>
      {label}
    </div>
    <div style={{ fontFamily: "var(--font-mono)", fontSize: 18, fontWeight: 700, color: color || "var(--fg-default)" }}>
      {value}
    </div>
  </div>
);

/* ---------------- Pipeline ---------------- */
const ABPipeline = ({ partial }) => {
  const steps = [
    { id: "received", label: "Received · Rx ✓" },
    { id: "allocbill", label: partial ? "Allocation & Billing · Partial" : "Allocation & Billing" },
    { id: "confirm", label: "Confirm orders" },
    { id: "consign", label: "Consignment" },
    { id: "packed", label: "Packed" },
  ];
  const idx = 1;
  return (
    <div style={{ display: "flex", gap: 0, alignItems: "center", maxWidth: 880 }}>
      {steps.map((s, i) => {
        const done = i < idx;
        const isCurrent = i === idx;
        return (
          <React.Fragment key={s.id}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
              <span
                style={{
                  width: 20,
                  height: 20,
                  borderRadius: 99,
                  background: done
                    ? "var(--green-500)"
                    : isCurrent
                      ? partial
                        ? "var(--amber-500)"
                        : "var(--ink-600)"
                      : "var(--bg-sunken)",
                  color: done || isCurrent ? "#fff" : "var(--fg-muted)",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 10,
                  fontWeight: 700,
                  fontFamily: "var(--font-mono)",
                  boxShadow: isCurrent ? `0 0 0 3px ${partial ? "var(--amber-50)" : "var(--ink-50)"}` : "none",
                }}
              >
                {done ? <IconCheck size={12} /> : i + 1}
              </span>
              <span
                style={{
                  fontSize: 11,
                  fontWeight: isCurrent ? 700 : 600,
                  color: done
                    ? "var(--green-700)"
                    : isCurrent
                      ? partial
                        ? "var(--amber-700)"
                        : "var(--ink-700)"
                      : "var(--fg-muted)",
                  whiteSpace: "nowrap",
                }}
              >
                {s.label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <span
                style={{
                  flex: 1,
                  minWidth: 18,
                  height: 2,
                  background: done ? "var(--green-300)" : "var(--bg-sunken)",
                  margin: "0 8px",
                }}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default AllocationBilling;
